import { configureStore } from '@reduxjs/toolkit'
import { useDispatch, useSelector } from 'react-redux'
import type { TypedUseSelectorHook } from 'react-redux'
import { persistStore, persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import currentReducer from './current'
import doctorReducer from './doctor'
import accessAuthReducer from './flows/auth/access/index'
import adminAuthReducer from './flows/auth/admin/index'
import doctorAuthReducer from './flows/auth/doctor/index'
import studioAuthReducer from './flows/auth/studio/index'
import currentPatientPortalReducer from './flows/portal/currentPatient'
import doctorPortalReducer from './flows/portal/doctor'
import portalReducer from './flows/portal/index'
import lobbyPortalReducer from './flows/portal/lobby/index'
import notesPortalReducer from './flows/portal/notes'
import studioPortalReducer from './flows/portal/studio'
import lobbyReducer from './lobby'
import meReducer from './me'
import notesReducer from './notes'
import scanReducer from './scan'
import scheduleReducer from './schedule'
import shopReducer from './shop'
import studioReducer from './studio'
// V1 reducers

// Persist configs for v1 slices
const currentPatientPortalPersistConfig = {
  key: 'currentPatientPortal',
  storage,
}

const doctorPortalPersistConfig = {
  key: 'doctorPortal',
  storage,
}

const lobbyPortalPersistConfig = {
  key: 'lobbyPortal',
  storage,
  blacklist: ['queue'],
}

const notesPortalPersistConfig = {
  key: 'notesPortal',
  storage,
}

const studioPortalPersistConfig = {
  key: 'studioPortal',
  storage,
}

const accessAuthPersistConfig = {
  key: 'accessAuth',
  storage,
}

const adminAuthPersistConfig = {
  key: 'adminAuth',
  storage,
}

const doctorAuthPersistConfig = {
  key: 'doctorAuth',
  storage,
}

const studioAuthPersistConfig = {
  key: 'studioAuth',
  storage,
}

const portalPersistConfig = {
  key: 'portal',
  storage,
}

// Create persisted reducers for v1 slices
const persistedCurrentPatientPortalReducer = persistReducer(
  currentPatientPortalPersistConfig,
  currentPatientPortalReducer,
)
const persistedDoctorPortalReducer = persistReducer(doctorPortalPersistConfig, doctorPortalReducer)
const persistedLobbyPortalReducer = persistReducer(lobbyPortalPersistConfig, lobbyPortalReducer)
const persistedNotesPortalReducer = persistReducer(notesPortalPersistConfig, notesPortalReducer)
const persistedStudioPortalReducer = persistReducer(studioPortalPersistConfig, studioPortalReducer)
const persistedAccessAuthReducer = persistReducer(accessAuthPersistConfig, accessAuthReducer)
const persistedAdminAuthReducer = persistReducer(adminAuthPersistConfig, adminAuthReducer)
const persistedDoctorAuthReducer = persistReducer(doctorAuthPersistConfig, doctorAuthReducer)
const persistedStudioAuthReducer = persistReducer(studioAuthPersistConfig, studioAuthReducer)
const persistedPortalReducer = persistReducer(portalPersistConfig, portalReducer)
export const store = configureStore({
  reducer: {
    // Old reducers
    studio: studioReducer,
    doctor: doctorReducer,
    lobby: lobbyReducer,
    current: currentReducer,
    notes: notesReducer,
    me: meReducer,
    scan: scanReducer,
    schedule: scheduleReducer,
    shop: shopReducer,
    // V1 persisted slices
    currentPatientPortal: persistedCurrentPatientPortalReducer,
    doctorPortal: persistedDoctorPortalReducer,
    lobbyPortal: persistedLobbyPortalReducer,
    notesPortal: persistedNotesPortalReducer,
    studioPortal: persistedStudioPortalReducer,
    accessAuth: persistedAccessAuthReducer,
    adminAuth: persistedAdminAuthReducer,
    doctorAuth: persistedDoctorAuthReducer,
    studioAuth: persistedStudioAuthReducer,
    portal: persistedPortalReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }),
})

export const persistor = persistStore(store)

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch

export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
